/* eslint-disable no-useless-escape */
import { AxiosResponse } from 'axios';
import classNames from 'classnames';
import { default as dayjs } from 'dayjs';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TmFavouriteLocalStorage } from '@/tscapi/model/favourite';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

import { trackFBSearchEvent } from '../tracking_third_party/fbSearch';

import AdCard, { AdCardProps } from './AdCard/AdCard';
import FraudModal from './FraudModal/FraudModal';
import { en, fr } from './i18n';
import { IDimension, IExpandedPanel, IProduct } from './IProduct';
import { IPrp, IPrpDimensionStateRefinement, IPrpDimensionState, IPrpPaging, IPrpResponse } from './IPrp';
import ProductCard, { ProductCategoryType } from './ProductCard/ProductCard';
import prpMock from './Prp.mock';
import OnAirButtons from './PrpFilterPanel/OnAirButtons';
import PrpFilterPanel, { ratingSvgMapping } from './PrpFilterPanel/PrpFilterPanel';
import PrpNoSearchResults from './PrpNoSearchResults/PrpNoSearchResults';

export interface PrpProps {
    data: IPrp;
}

export type FocusArea = 'products' | 'filter' | 'sort';

const Prp = () => {
    const { i18n, t } = useTranslation('prp');
    i18n.addResourceBundle('en', 'prp', en, true, true);
    i18n.addResourceBundle('fr', 'prp', fr, true, true);

    const BREAKPOINT = 1023;
    const DEFAULT_PAGE_SIZE = 48;
    const DEFAULT_PAGE_SORT_KEY = 'FeaturedProducts';
    const DEFAULT_DIMENSION_ID = '0';
    const PRP_CARD_GRID_ITEM_CLASSNAME = '.prp-card-grid-item';

    const [data, setData] = useState<IPrp>({
        ...prpMock.data,
        Products: [],
        DimensionStates: [],
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSortKey, setPageSortKey] = useState('');
    const [keyword, setKeyword] = useState('');
    const [rdParameter, setRdParameter] = useState('');
    const [viParameter, setViParameter] = useState('');
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [currentFocusedCardIndex, setCurrentFocusedCardIndex] = useState(0);
    const [favourites, setFavourites] = useState<TmFavouriteLocalStorage[] | null>(null);
    const [currentFocusArea, setCurrentFocusArea] = useState<FocusArea>('products');
    const [isFetchComplete, setIsFetchComplete] = useState(false);
    const [showFraudModal, setShowFraudModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [jsonLD, setJsonLD] = useState('');
    const [dimensions, setDimensions] = useState<IDimension[]>([]);
    const [expandedFilter, setExpandedFilter] = useState<IExpandedPanel[]>([]);
    const dimensionTypes: string[] = [
        //dimension urlSlugs
        'colour',
        'brand',
        'size',
        'price',
        'metal',
        'stone',
        'review', //Exception: Algolia typeId is not matching
        'clearance',
        'collection',
        'price-label',
        'vendor-type', //Exception: Algolia typeId is not matching
        'marketplace-vendor', //Exception: Algolia typeId is not matching
        'new-arrival', //Exception: Algolia typeId is not matching
        'league', //Exception: Algolia typeId is not matching
        'category',
        'team',
        'player',
    ];

    const [isAdCardAvailable, setIsAdCardAvailable] = useState(false);
    const [adCardProps, setAdCardProps] = useState<AdCardProps | null>(null);

    /*
    console.log('%c data.DimensionStates:', 'color: purple; font-weight: bold;');
    console.log({ dataDimensionStates: data.DimensionStates });
    */
    /*
  console.log('%c pageDimensionId:', 'color: magenta; font-weight: bold;');
  console.log({ pageDimensionId });
  */

    const getProductImageURL = (prod: IProduct) => {
        if (prod != null && prod.ImageUrl != null && prod.ImageUrl.length > 0) {
            return 'https://item.tscimg.ca/TSC/' + prod.ImageUrl + '?impolicy=XL';
        }
        return '';
    };

    const getEdpNo = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.EdpNo;
            }
        }
        return '';
    };

    const getUpcNo = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.UpcNo;
            }
        }
        return '';
    };

    const getAppliedPrice = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.AppliedPrice || '0.00';
            }
        }
        return '';
    };

    const getSafeHTML = (value: object) => {
        // If value convert to JSON and escape / to prevent script tag in JSON
        const json = value ? JSON.stringify(value, null) : ''; /*.replace(/\//g, '\\/')*/
        return DOMPurify.sanitize(json);
    };

    const getBaseUrl = (): string => {
        switch (window.location.href.substring(0, window.location.href.indexOf('.'))) {
            case 'https://lo-tsc':
                return 'https://lo-api.tsc.ca';
            case 'https://qa-tsc':
                return 'https://qa-api.tsc.ca';
            case 'https://qa-tsc-pre':
                return 'https://qa-api-pre.tsc.ca';
            case 'https://st-tsc':
                return 'https://st-api.tsc.ca';
            case 'https://st-tsc-pre':
                return 'https://st-api-pre.tsc.ca';
            case 'https://pr-tsc-pre':
                return 'https://api-pre.tsc.ca';
            case 'https://tsc':
            case 'https://www':
                return 'https://api.tsc.ca';
            default:
                return 'https://api.tsc.ca';
        }
    };

    const updateSchema = (prodResult: IPrp) => {
        //debugger;
        const todayDate: Date = new Date(Date.now());
        function makeDate(date: Date) {
            return new Date(+date);
        }

        const todaysDateFormatted = dayjs(makeDate(todayDate)).format('YYYY-MM-DD');
        const oneWeekLaterDateFormatted = dayjs(makeDate(todayDate)).add(7, 'day').format('YYYY-MM-DD');

        //let dateToString = formatWithOptions('yyyy-MM-dd');
        if (prodResult.Products == null || prodResult.Products.length == 0) {
            setJsonLD('');
            return;
        }

        //Construct Schema's mainEntity object
        const schema = {
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            mainEntity: [],
        };

        const objEntity = {
            '@type': 'ItemList',
            name:
                prodResult.DimensionStates != null && prodResult.DimensionStates.length > 0
                    ? prodResult.DimensionStates[0].ListTitle
                    : 'ItemList',
            itemListOrder:
                'https://schema.org/ItemListOrderAscending' /* Order is not available in TmProductResult   */,
            itemListElement: [],
        };

        let position = 0;
        const cookie = new Cookie();
        const language = cookie.Language.toLowerCase();
        const getUrl = window.location;
        const baseUrl = getUrl.protocol + '//' + getUrl.host;

        for (const product of prodResult.Products) {
            position = position + 1;

            const oProd: { [k: string]: any } = {
                '@type': 'ListItem',
                position: position,
                item: {
                    '@type': 'Product',
                    name: product.Name,
                    image: getProductImageURL(product),
                    description: product.Name /* description is not available in TmProductForPRP, so Name is used  */,
                    brand: product.Brand != null ? product.Brand : 'Brand not provided',
                    offers: {
                        '@type': 'Offer',
                        priceCurrency: 'CAD',
                        url: baseUrl + product.LinkWithoutDomain + '&edp=' + getEdpNo(product),
                        itemCondition: 'https://schema.org/NewCondition',
                        priceValidUntil: product.IsShowstopper ? todaysDateFormatted : oneWeekLaterDateFormatted,
                        price:
                            language == 'en_ca'
                                ? getAppliedPrice(product).replace(/[^0-9\.]+/g, '')
                                : getAppliedPrice(product)
                                      .replace(/[^0-9\,]+/g, '')
                                      .replace(',', '.'),
                        availability: 'https://schema.org/InStock',
                    },
                },
            };

            if (getUpcNo(product)) {
                oProd.item.gtin12 = getUpcNo(product);
            }
            if (getEdpNo(product)) {
                oProd.item.sku = getEdpNo(product);
            }

            if (product.ProductReviewCount > 0) {
                const oAggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: product.ProductReviewRating,
                    bestRating: '5',
                    worstRating: '0',
                    ratingCount: product.ProductReviewCount,
                };
                oProd.item['aggregateRating'] = Object.assign({}, oAggregateRating);
            }

            objEntity.itemListElement.push(oProd);
        }

        schema.mainEntity.push(objEntity);
        const json = schema;
        setJsonLD(getSafeHTML(json));
    };

    const filterPanelHandler = () => {
        if (isFilterPanelOpen) {
            setIsFilterPanelOpen(false);
        } else {
            setIsFilterPanelOpen(true);
        }
    };

    const getUserFavourites = (edp: number) => {
        const favouriteStorage = new FavouriteStorage();
        favouriteStorage.Favourites;
        setFavourites(favouriteStorage.Favourites);
    };

    const buildApiUrl = useCallback(
        (
            page: number,
            sortKey: string,
            keywordKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
            dimensions: IDimension[],
        ) => {
            const lang = new Cookie().Language.substring(0, 2) || 'en';
            const baseUrl = getBaseUrl() + '/product/v1/' + lang;
            let visitorId = '';
            if (viValue !== '') {
                visitorId = `visitorId=${viValue}`;
            }
            const filters = [];
            dimensionTypes.forEach((type) => {
                type = type.toLowerCase();
                if (dimensions.length > 0) {
                    const dimension = dimensions.find((d) => d.Type === type);
                    if (dimension && dimension.UrlSlugs.length > 0) {
                        filters.push(
                            `${type}=${
                                dimension.UrlSlugs.length > 1 ? dimension.UrlSlugs.join(',') : dimension.UrlSlugs[0]
                            }`,
                        );
                    }
                }
            });
            const pageParam = page === 1 ? '' : `page=${page}`;
            const pageSizeParam = pageSize === DEFAULT_PAGE_SIZE ? '' : `pageSize=${pageSize}`;
            const sortKeyParam = sortKey !== '' && sortKey !== DEFAULT_PAGE_SORT_KEY ? 'sortKey=' + sortKey : '';
            const keywordParam = keywordKey !== '' ? 'keyword=' + keywordKey : '';
            const rdParam = rdValue !== '' ? 'rd=' + rdValue : '';
            const params = [visitorId, pageParam, pageSizeParam, sortKeyParam, keywordParam, rdParam].filter(
                (it) => it !== '',
            );
            params.push(...filters);
            const concat = params.join('&');
            const endpoint = concat !== '' ? `?${concat}` : '';
            let pathname = '';
            if (
                window.location.pathname.length !== window.location.pathname.indexOf('products') + 8 &&
                window.location.pathname.length !== window.location.pathname.indexOf('products') + 9
            ) {
                pathname = window.location.pathname.substring(window.location.pathname.indexOf('products/') + 9);
            }
            const prpUrl = baseUrl + `/products/${pathname}${endpoint}`;
            return prpUrl;
        },
        [],
    );

    const buildPageUrl = useCallback(
        (
            page: number,
            sortKey: string,
            keywordKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
            dimensions: IDimension[],
        ) => {
            /*
    console.log('%c dimensionId:', 'color: brown; font-weight: bold;');
    console.log({ dimensionId });
    */
            const baseUrl = '';
            let visitorId = '';
            if (viValue !== '') {
                visitorId = `nav=vi:${viValue}`;
            }
            const filters = [];
            dimensionTypes.forEach((type) => {
                type = type.toLowerCase();
                if (dimensions.length > 0) {
                    const dimension = dimensions.find((d) => d.Type === type);
                    if (dimension && dimension.UrlSlugs.length > 0) {
                        filters.push(
                            `${type}=${
                                dimension.UrlSlugs.length > 1 ? dimension.UrlSlugs.join(',') : dimension.UrlSlugs[0]
                            }`,
                        );
                    }
                }
            });
            const pageParam = page === 1 ? '' : `page=${page}`;
            const pageSizeParam = pageSize === DEFAULT_PAGE_SIZE ? '' : `pageSize=${pageSize}`;
            const sortKeyParam = sortKey !== '' && sortKey !== DEFAULT_PAGE_SORT_KEY ? 'sortKey=' + sortKey : '';
            const keywordParam = keywordKey !== '' ? 'keyword=' + keywordKey : '';
            const rdParam = rdValue !== '' ? 'rd=' + rdValue : '';
            const params = [visitorId, pageParam, pageSizeParam, sortKeyParam, keywordParam, rdParam].filter(
                (it) => it !== '',
            );
            params.push(...filters);
            const concat = params.join('&');
            const endpoint = concat !== '' ? `?${concat}` : '';
            let pathname = '';
            if (
                window.location.pathname.length !== window.location.pathname.indexOf('products') + 8 &&
                window.location.pathname.length !== window.location.pathname.indexOf('products') + 9
            ) {
                pathname = window.location.pathname.substring(window.location.pathname.indexOf('products/') + 9);
            }
            const pageUrl = baseUrl + `/products/${pathname}${endpoint}`;
            return pageUrl;
        },
        [],
    );

    const setProductRefinementAnalytics = (searchTerm: string, paging: IPrpPaging) => {
        const { TotalRecords, TotalPages, CurrentPage } = paging;
        const searchObj = { searchTerm, totalRecords: TotalRecords, totalPages: TotalPages, currentPage: CurrentPage };
        window.analyticsData.event = window.analyticsData.event || {};
        window.analyticsData.event.type = 'prp_refinement';
        window.analyticsData.search = searchObj;
    };

    const setMissingProductAnalytics = (searchTerm: string) => {
        const missingProductObj = { itemNo: searchTerm };
        const searchObj = { searchTerm, totalRecords: 0, totalPages: 0, currentPage: 0 };
        window.analyticsData.missingProduct = missingProductObj;
        window.analyticsData.search = searchObj;
    };

    const scrollToTopOfPRP = () => {
        const toScrollPosition = document.getElementById('to-scroll-position');
        toScrollPosition.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const navigateToPage = useCallback(
        async (
            page: number,
            sortKey: string,
            keyword: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
            dimensions: IDimension[],
        ): Promise<IPrpResponse> => {
            const cachebleHeaders = {
                'Content-Type': 'application/json',
                currentdatetime: new Cookie().CurrentDate || new Date().toISOString(),
            };

            const prpUrl = buildApiUrl(page, sortKey, keyword, pageSize, rdValue, viValue, dimensions);

            let result: AxiosResponse<IPrp, any>;
            const removeDims = [];

            try {
                result = await http.get<IPrp>(prpUrl, {
                    headers: cachebleHeaders,
                    transformRequest: (data, headers) => {
                        delete headers.Authorization;
                        return data;
                    },
                });

                const mybrands = dimensions.filter((x) => x.Type.toLowerCase() === 'brand');
                if (mybrands.length > 0 && mybrands[0].UrlSlugs.length > 0) {
                    mybrands[0].UrlSlugs.forEach((brand) => {
                        const brandstate = result.data.DimensionStates.find(
                            (x) => x.DimensionType.toLowerCase() === 'brand',
                        );
                        if (brandstate) {
                            const ref = brandstate.Refinements.find((x) => x.UrlSlug === brand);
                            if (!ref) {
                                removeDims.push(brand);
                            }
                        }
                    });
                    mybrands[0].UrlSlugs = mybrands[0].UrlSlugs.filter((x) => !removeDims.includes(x));
                }

                setDimensions(dimensions);
                setData(result.data);
                setCurrentPage(result.data.Paging.CurrentPage);
                setPageSortKey(result.data.Sorting.SelectedSortOption.Key);
                updateSchema(result.data);
                generateAnalyticsFilters(result.data.DimensionStates);
                if (result.data.Products.length > 0)
                    setProductRefinementAnalytics(result.data.SearchTerm, result.data.Paging);
                if (result.data.Products === null || result.data.Products.length === 0)
                    setMissingProductAnalytics(result.data.SearchTerm);
                setIsFetchComplete(true);
            } catch (e) {
                setIsFetchComplete(true);
            }

            const resp: IPrpResponse = {
                url: buildPageUrl(page, sortKey, keyword, pageSize, rdValue, viValue, dimensions),
                dimensionStates: result.data.DimensionStates,
            };

            if (removeDims && removeDims.length > 0) {
                window.history.pushState({}, '', resp.url);
            }
            return resp;
        },
        [setData],
    );

    const fetchAndSetProducts = useCallback(
        async (
            page: number,
            sortKey: string,
            keywordKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
            dimensions: IDimension[],
        ): Promise<IPrpResponse> => {
            return await navigateToPage(page, sortKey, keywordKey, pageSize, rdValue, viValue, dimensions);
        },
        [navigateToPage],
    );

    useEffect(() => {
        if (currentPage > data.Paging.TotalPages && data.Paging.TotalPages > 0) {
            fetchAndSetProducts(
                data.Paging.TotalPages,
                pageSortKey,
                keyword,
                currentPageSize,
                rdParameter,
                '',
                dimensions,
            );
        }
    }, [currentPage, data.Paging.TotalPages]);

    useEffect(() => {
        try {
            const searchString = window.sessionStorage.getItem('TSC_SearchString');
            if (searchString) {
                trackFBSearchEvent(searchString);
                window.sessionStorage.removeItem('TSC_SearchString');
            }
        } catch (error) {
            console.error('Error accessing sessionStorage:', error);
        }
    }, []);

    useEffect(() => {
        const domAdCard = document.querySelector<HTMLAnchorElement>('.ad-card');

        const adCardData: AdCardProps = {
            ctaText: '',
            href: '',
            image: '',
            text: '',
            title: '',
            imageAlt: '',
            textBackgroundColour: '',
            textBackgroundOpacity: 0.5,
            textColour: '',
        };
        const queryString = window.location.search.slice(1);
        const queries: Record<string, string> = {};
        queryString.split('&').forEach((query) => {
            const [key, value] = query.split('=');
            queries[key] = value;
        });

        const pageSizeFromQuery = queries['pageSize'] || `$${DEFAULT_PAGE_SIZE}`;
        const parsedPageSize = parseInt(pageSizeFromQuery, 10) || DEFAULT_PAGE_SIZE;
        let pageSize = parsedPageSize;
        setCurrentPageSize(pageSize);

        if (!domAdCard) {
            setIsAdCardAvailable(false);
        } else {
            setIsAdCardAvailable(true);
            const domAdCardImage = document.querySelector<HTMLImageElement>('.ad-card__self');
            const domAdCardTitle = document.querySelector<HTMLSpanElement>('.ad-card__content-title');
            const domAdCardText = document.querySelector<HTMLSpanElement>('.ad-card__content-text');
            const domAdCardCta = document.querySelector<HTMLSpanElement>('.ad-card__content-cta__title');
            const domAdCardImageAltText = document.querySelector<HTMLSpanElement>('.ad-card__image-alt-text');
            const domAdCardTextColour = document.querySelector<HTMLSpanElement>('.ad-card__text-colour');
            const domAdCardTextBackgroundColour = document.querySelector<HTMLSpanElement>(
                '.ad-card__text-background-colour',
            );
            const domAdCardTextBackgroundOpacity = document.querySelector<HTMLSpanElement>(
                '.ad-card__text-background-opacity',
            );

            adCardData.href = domAdCard.getAttribute('href') || '';
            adCardData.image = domAdCardImage?.getAttribute('src') || '';
            adCardData.title = domAdCardTitle.textContent || '';
            adCardData.text = domAdCardText.textContent || '';
            adCardData.ctaText = domAdCardCta.textContent || '';
            adCardData.imageAlt = domAdCardImageAltText.textContent || '';
            adCardData.textColour = domAdCardTextColour.textContent || '';
            adCardData.textBackgroundColour = domAdCardTextBackgroundColour.textContent || '';
            adCardData.textBackgroundOpacity = parseFloat(domAdCardTextBackgroundOpacity.textContent) || 0.5;

            setAdCardProps(adCardData);
            domAdCard.remove();

            pageSize -= 1;
            setCurrentPageSize(pageSize);
        }

        let navQuery = queries['nav'];
        let viValue = '';
        const pathname = window.location.pathname.substring(window.location.pathname.indexOf('products/') + 9);
        const paths = pathname.split('/');

        if (navQuery) {
            navQuery = decodeURIComponent(navQuery);
            const [head, tail] = navQuery.split(':');
            if (head === 'vi') {
                //dimensionId = tail;
                setViParameter(tail);
                viValue = tail;
            }
        }

        const pageQuery = queries['page'];
        let page = 1;

        if (pageQuery) {
            page = parseInt(pageQuery, 10) || 1;
            setCurrentPage(page);
        }

        const sortKey = queries['sortKey'] || '';
        setPageSortKey(sortKey);

        const keyword = queries['keyword'] || '';
        setKeyword(keyword);

        const rdTemp = queries['rd'] || '';
        const rd = rdTemp === 'false' ? '0' : rdTemp;
        setRdParameter(rd);

        const dimensionObject: IDimension[] = [];

        dimensionTypes.forEach((type) => {
            type = type.toLowerCase();
            const filterByDimension = queries[type] || '';
            const filterByDimensionArray = filterByDimension.split(',');
            const dims = filterByDimensionArray;

            if (dims.length > 0 && filterByDimension !== '') {
                dims.forEach((dim: string) => {
                    const dimension = dimensionObject.find((d) => d.Type === type) as IDimension;
                    if (dimension && !dimension.UrlSlugs.includes(dim)) {
                        dimension.UrlSlugs.push(dim);
                    } else if (!dimension || !dimension.UrlSlugs.includes(dim)) {
                        dimensionObject.push({ Type: type, UrlSlugs: [dim] });
                    }
                });
            }
        });

        setDimensions(dimensionObject);

        fetchAndSetProducts(page, sortKey, keyword, pageSize, rd, viValue, dimensionObject).then(
            (prpResponse: IPrpResponse) => {
                if (prpResponse.dimensionStates) {
                    prpResponse.dimensionStates.forEach((state) => {
                        state.Refinements.forEach((refinement) => {
                            const includedDimensions = dimensions.filter(
                                (x) =>
                                    (x.Type.toLowerCase() === refinement.DimensionType.toLowerCase() &&
                                        x.Type.toLowerCase() === 'brand' &&
                                        x.UrlSlugs.includes(refinement.UrlSlug)) ||
                                    (x.Type.toLowerCase() !== 'brand' &&
                                        x.Type.toLowerCase() === refinement.DimensionType.toLowerCase()),
                            );
                            const excluded = includedDimensions.length === 0;
                            if (refinement.Selected && excluded) {
                                setExpandedFilter((prev: IExpandedPanel[]) => {
                                    const dim = prev.find(
                                        (x) => x.Type.toLowerCase() === refinement.DimensionType.toLowerCase(),
                                    );
                                    if (dim) {
                                        dim.Expanded = true;
                                    } else {
                                        prev.push({ Type: refinement.DimensionType, Expanded: true });
                                    }
                                    return prev;
                                });
                            }
                        });
                    });
                }

                const uat = new Cookie().UserAccessToken;
                if (!uat) {
                    return;
                }

                const customerEdp = uat.Edp;
                if (!customerEdp) {
                    return;
                }

                getUserFavourites(customerEdp);
            },
        );
    }, [fetchAndSetProducts]);

    const isSignedIn = useMemo(() => {
        const cookie = new Cookie();
        return !!cookie.UserAccessToken;
    }, [Cookie]);

    const isFavourite = (itemNo: string) => {
        if (!isSignedIn) {
            return false;
        }

        return favourites?.some((item) => item.ItemNumber === itemNo);
    };

    const products = useMemo(() => {
        return data.Products.map((el, i) => {
            const isLiked = isFavourite(el.ItemNo) || false;
            return (
                <div
                    className="prp__relative prp-card-grid-item prp-card-grid-item--product-card"
                    key={el.LinkWithoutDomain}
                >
                    <ProductCard
                        header={{ isLiked }}
                        productObj={el}
                        productHref={el.LinkWithoutDomain}
                        productImage={el.ImageUrl}
                        name={el.Name}
                        availableSwatches={el.AvailableSwatches}
                        brandName={el.Brand}
                        isPriceRange={el.IsPriceRange}
                        wasPriceRange={el.WasPriceRange}
                        reviewCount={el.ProductReviewCount}
                        rating={el.ProductReviewRating}
                        sizes={el.Sizes}
                        styles={el.Styles}
                        edps={el.Edps}
                        categoryPath={el.CategoryPath as ProductCategoryType}
                        enabledAddToCart={el.EnabledAddToCart}
                        isShowstopper={el.IsShowstopper}
                        showBadgeImage={el.ShowBadgeImage}
                        badgeImagePath={el.BadgeImagePath}
                        itemNo={el.ItemNo}
                        priceIsLabel={el.PriceIsLabel}
                        edp={el.DefaultEdp}
                        position={i}
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        queryId={data?.QueryId || null}
                        sellerType={el.SellerType}
                        showFraudModal={() => {
                            document.querySelector<HTMLElement>('.prp-wrapper')?.scrollIntoView();
                            setShowFraudModal(true);
                            $('#fraudModal').modal('show');
                        }}
                    />
                </div>
            );
        });
    }, [data, favourites]);

    const cards = useMemo(() => {
        const MOBILE_BREAKPOINT = 768;
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const tmp: JSX.Element[] = [];

        if (isAdCardAvailable && adCardProps !== null) {
            const adCardComponent = (
                <AdCard
                    {...adCardProps}
                    key={adCardProps.image}
                    className="prp-card-grid-item prp-card-grid-item--ad-card"
                />
            );

            if (isMobile) {
                tmp.push(adCardComponent);
                tmp.push(products[0]);
                tmp.push(products[1]);
            } else {
                tmp.push(products[0]);
                tmp.push(products[1]);
                tmp.push(adCardComponent);
            }

            products.slice(2).forEach((p) => {
                tmp.push(p);
            });
        } else {
            products.forEach((p) => {
                tmp.push(p);
            });
        }

        return tmp.slice(0, currentPageSize);
    }, [products, currentPageSize, adCardProps, window.innerWidth]);

    // const scrollToTop = useCallback(() => {
    //   // window.scrollTo({
    //   //   top: 0,
    //   //   left: 0,
    //   //   behavior: 'smooth',
    //   // });

    //   document.querySelector<HTMLElement>('.prp')?.scrollIntoView();
    // }, []);

    const productGridKeyListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const UP_ARROW = 'ArrowUp';
        const RIGHT_ARROW = 'ArrowRight';
        const DOWN_ARROW = 'ArrowDown';
        const LEFT_ARROW = 'ArrowLeft';

        let newIndex = -1;
        if (e.key === UP_ARROW) {
            newIndex = currentFocusedCardIndex - 3;
        } else if (e.key === RIGHT_ARROW) {
            newIndex = currentFocusedCardIndex + 1;
        } else if (e.key === DOWN_ARROW) {
            newIndex = currentFocusedCardIndex + 3;
        } else if (e.key === LEFT_ARROW) {
            newIndex = currentFocusedCardIndex - 1;
        }

        if (newIndex < 0) {
            newIndex = 0;
        } else if (newIndex >= cards.length) {
            newIndex = cards.length - 1;
        }

        setCurrentFocusedCardIndex(newIndex);
    };

    const prpKeyDownListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!e.ctrlKey) {
            return;
        }

        if (currentFocusArea === 'products') {
            setCurrentFocusArea('filter');
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            const el = document.querySelector<HTMLElement>('.prp-filter-panel__blocks');
            el?.focus();
            return;
        }

        if (currentFocusArea === 'filter') {
            setCurrentFocusArea('sort');
            document.querySelector<HTMLElement>('.prp__count-and-shorting__shorting')?.focus();
            return;
        }

        if (currentFocusArea === 'sort') {
            setCurrentFocusArea('products');
            document.querySelector<HTMLElement>(PRP_CARD_GRID_ITEM_CLASSNAME)?.focus();
            return;
        }
    };

    const updateFilterAnalyticsData = (selectedFilters: any[]) => {
        const analyticsData = window.analyticsData || {};
        analyticsData.filters = selectedFilters;
        window.analytics_track = window.analytics_track || [];
        window.analytics_track.push({
            event: {
                type: 'prp_refinement',
            },
        });
    };

    const generateAnalyticsFilters = (dimensionStates: IPrpDimensionState[]) => {
        if (dimensionStates != null && dimensionStates.length > 0) {
            // let totalSelectedRefinements = 0;

            const analyticsFilters = [];

            for (const dimState of dimensionStates) {
                const groupName: string = dimState.ListTitle;

                dimState.ListTitle = dimState.ListTitle.substr(
                    0,
                    dimState.ListTitle.indexOf('(') > 0 ? dimState.ListTitle.indexOf('(') : dimState.ListTitle.length,
                ).trim();
                const selectedRefinements: number = dimState.Refinements.filter((dim) => dim.Selected).length;
                if (selectedRefinements > 0) {
                    dimState.ListTitle += ' (' + selectedRefinements + ')';
                    // totalSelectedRefinements += selectedRefinements;

                    const options = [];
                    const selectedFilters = dimState.Refinements.filter((dim) => dim.Selected);
                    for (const selectedFilter of selectedFilters) {
                        options.push({ name: selectedFilter.DimensionName });
                    }
                    analyticsFilters.push({ group: groupName, options: options });
                }
            }
            /*
      console.log('%c analyticsFilters:', 'color: purple; font-weight: bold;');
      console.log({ analyticsFilters });
      */
            updateFilterAnalyticsData(analyticsFilters);
        }
    };

    useEffect(() => {
        // window.addEventListener('resize', () => setWidth(window.innerWidth));
        if (width <= BREAKPOINT) {
            setIsFilterPanelOpen(false);
        } else {
            setIsFilterPanelOpen(true);
        }
    }, [width, BREAKPOINT, setIsFilterPanelOpen, setWidth]);

    useEffect(() => {
        const interval = setInterval(() => {
            setWidth(window.innerWidth);
        }, 300);

        return () => {
            clearInterval(interval);
        };
    }, [setWidth]);

    useEffect(() => {
        const allGridItems = document.querySelectorAll<HTMLElement>(PRP_CARD_GRID_ITEM_CLASSNAME);
        const currentItem = allGridItems[currentFocusedCardIndex];
        const isProductCard = currentItem?.classList.contains('prp-card-grid-item--product-card');

        if (isProductCard) {
            const index = currentFocusedCardIndex >= 2 ? currentFocusedCardIndex - 1 : currentFocusedCardIndex;
            document.querySelectorAll<HTMLElement>('.product-card')[index]?.focus();
        } else {
            document.querySelectorAll<HTMLAnchorElement>('.ad-card')[0]?.focus();
        }
    }, [currentFocusedCardIndex]);

    const onBackButtonEvent = (event: PopStateEvent) => {
        event.preventDefault();
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href;
    };

    useEffect(() => {
        window.addEventListener('popstate', onBackButtonEvent);
        return () => window.removeEventListener('popstate', onBackButtonEvent);
    }, []);

    const paginationIndices = useMemo(() => {
        const indices = [];
        const halfSize = 5;
        for (let i = 0; i < halfSize; i++) {
            indices.push(currentPage - i);
        }

        for (let i = 1; i < 2 * halfSize; i++) {
            indices.push(currentPage + i);
        }

        const maxPage = data.Paging.TotalPages <= 10 ? data.Paging.TotalPages : 10;
        const filtered = indices.filter((i) => i > 0 && i <= data.Paging.TotalPages).sort((a, b) => a - b);
        const arr: number[] = [];

        for (let i = 0; i < Math.min(maxPage, filtered.length); i++) {
            arr.push(filtered[i]);
        }

        return arr;
    }, [currentPage, data.Paging.TotalPages]);

    const appliedFilters = useMemo(() => {
        const filters: Array<IPrpDimensionStateRefinement> = [];
        if (!data.DimensionStates) {
            return [];
        }
        data.DimensionStates.forEach((state) => {
            state.Refinements.forEach((refinement) => {
                /*
        console.log('%c refinement:', 'color: lime; font-weight: bold;');
        console.log({ refinement });
        */
                if (refinement.Selected) {
                    filters.push(refinement);
                }
            });
        });
        return filters;
    }, [data, data.DimensionStates]);

    const removedFilters = useMemo(() => {
        const filters: Array<IPrpDimensionStateRefinement> = [];
        if (!data.DimensionStates) {
            return [];
        }
        data.DimensionStates.forEach((state) => {
            state.Refinements.forEach((refinement) => {
                const includedDimensions = dimensions.filter(
                    (x) =>
                        (x.Type.toLowerCase() === refinement.DimensionType.replace(' ', '-').toLowerCase() &&
                            x.Type.toLowerCase() === 'brand' &&
                            x.UrlSlugs.includes(refinement.UrlSlug)) ||
                        (x.Type.toLowerCase() !== 'brand' &&
                            (refinement.DimensionType.replace(' ', '-').toLowerCase() === x.Type ||
                                (x.Type === 'vendortype' &&
                                    refinement.DimensionType.replace(' ', '-').toLowerCase() === 'vendor-type') ||
                                (x.Type === 'review' &&
                                    refinement.DimensionType.replace(' ', '-').toLowerCase() === 'reviews') ||
                                (x.Type === 'new-arrival' &&
                                    refinement.DimensionType.replace(' ', '-').toLowerCase() === 'newarrivals') ||
                                (x.Type === 'league' &&
                                    refinement.DimensionType.replace(' ', '-').toLowerCase() === 'leagues'))),
                );
                const excluded = includedDimensions.length === 0;
                if (refinement.Selected && !excluded) {
                    filters.push(refinement);
                }
            });
        });
        return filters;
    }, [data, data.DimensionStates]);

    const onFilterChange = (refinement: IPrpDimensionStateRefinement) => {
        const queryString = window.location.search.slice(1);
        const queries: Record<string, string> = {};
        queryString.split('&').forEach((query) => {
            const [key, value] = query.split('=');
            queries[key] = value;
        });

        const dimensionObject: IDimension[] = dimensions;

        dimensionTypes.forEach((type) => {
            type = type.toLowerCase();
            const filteredDimension = queries[type] || '';
            let filteredDimensionList = [];
            if (filteredDimension !== '') {
                filteredDimensionList = filteredDimension.split(',');
            }
            let filterByDimension: string[] = filteredDimensionList;
            // If not selected, then add a new selection
            // Else, remove the selection
            if (!refinement.Selected) {
                if (
                    refinement.DimensionType.replace(' ', '-').toLowerCase() === type ||
                    (type === 'vendortype' &&
                        refinement.DimensionType.replace(' ', '-').toLowerCase() === 'vendor-type') ||
                    (type === 'review' && refinement.DimensionType.replace(' ', '-').toLowerCase() === 'reviews') ||
                    (type === 'new-arrival' &&
                        refinement.DimensionType.replace(' ', '-').toLowerCase() === 'newarrivals') ||
                    (type === 'league' && refinement.DimensionType.replace(' ', '-').toLowerCase() === 'leagues')
                ) {
                    if (filterByDimension.length > 0) {
                        filterByDimension.push(refinement.UrlSlug);
                    } else {
                        filterByDimension = [refinement.UrlSlug];
                    }
                }
            } else {
                filterByDimension = filterByDimension.filter((x) => x !== refinement.UrlSlug);
            }

            const dimension = dimensionObject.find((d) => d.Type === type) as IDimension;
            if (dimension) {
                dimension.UrlSlugs = filterByDimension;
            } else {
                dimensionObject.push({ Type: type, UrlSlugs: filterByDimension });
            }
        });
        setDimensions(dimensionObject);

        const dimensionRefinement = data.DimensionStates.filter((dim) =>
            dim.Refinements.some((y) => y.UrlSlug == refinement.UrlSlug),
        );

        if (dimensionRefinement.length > 0) {
            const ref = dimensionRefinement[0].Refinements.filter((x) => x.UrlSlug == refinement.UrlSlug);
            ref[0].Selected = !ref[0].Selected;
            setData(data);
        }

        setIsLoading(true);
        fetchAndSetProducts(
            1,
            data.Sorting.SelectedSortOption.Key,
            keyword,
            currentPageSize,
            rdParameter,
            viParameter,
            dimensions,
        ).then((newPageUrl: IPrpResponse) => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl.url);
            setIsLoading(false);
        });
    };

    const removeAppliedFilter = (type: string, urlSlug: string) => {
        const dimensionObject: IDimension[] = dimensions;
        dimensionTypes.forEach((dimensionType) => {
            dimensionType = dimensionType.toLowerCase();
            if (
                dimensionType === type.replace(' ', '-').toLowerCase() ||
                (dimensionType === 'review' && type.replace(' ', '-').toLowerCase() === 'reviews') ||
                (dimensionType === 'new-arrival' && type.replace(' ', '-').toLowerCase() === 'newarrivals') ||
                (dimensionType === 'league' && type.replace(' ', '-').toLowerCase() === 'leagues')
            ) {
                const selectedDimemsions = data.DimensionStates.filter((dim) =>
                    dim.Refinements.some((ref) => ref.Selected),
                );

                const dims = selectedDimemsions.filter((dim) => dim.Refinements.some((y) => y.UrlSlug === urlSlug));

                if (dims.length > 0) {
                    const dimType = dimensionType;
                    const dimension = dimensionObject.find((d) => d.Type === dimType) as IDimension;
                    if (dimension) {
                        dimension.UrlSlugs.splice(dimension.UrlSlugs.indexOf(urlSlug), 1);
                    }
                }
            }
        });
        setDimensions(dimensionObject);
        setIsLoading(true);
        fetchAndSetProducts(
            1,
            data.Sorting.SelectedSortOption.Key,
            keyword,
            currentPageSize,
            rdParameter,
            viParameter,
            dimensionObject,
        ).then((newPageUrl: IPrpResponse) => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl.url);
            setIsLoading(false);
        });
    };

    const removeAllAppliedFilters = () => {
        setIsLoading(true);
        const dimensionObject: IDimension[] = dimensions;
        const collectionDim = dimensionObject.filter((dim) => dim.Type === 'collection' || dim.Type === 'category');
        fetchAndSetProducts(
            1,
            data.Sorting.SelectedSortOption.Key,
            keyword,
            currentPageSize,
            rdParameter,
            viParameter,
            collectionDim,
        ).then((newPageUrl: IPrpResponse) => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl.url);
            setIsLoading(false);
        });
    };

    const isExpandedFilter = (type: string, isExpanded: boolean) => {
        setExpandedFilter((prev: IExpandedPanel[]) => {
            const dim = prev.find((x) => x.Type === type);
            if (dim) {
                dim.Expanded = isExpanded;
            } else {
                prev.push({ Type: type, Expanded: isExpanded });
            }
            return prev;
        });
    };

    const isSearchPage = useMemo(() => {
        return keyword !== '';
    }, [keyword]);

    const isOnAirPage = useMemo(
        () => {
            return (
                /*tscConfig.AliasDimensionOnAirRecently === pageDimensionId[0] ||
                tscConfig.AliasDimensionOnAirToday === pageDimensionId[0] ||
                tscConfig.AliasDimensionOnAirYesterday === pageDimensionId[0]*/
                false
            );
        },
        [
            /*pageDimensionId*/
        ],
    );

    /*
  console.log('%c jsonLD, isJsonLDTrue:', 'color: red; font-weight: bold;');
  console.log({ jsonLD, isJsonLDTrue: jsonLD.length > 0 });
  */

    const sortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setIsLoading(true);
        fetchAndSetProducts(1, e.target.value, keyword, currentPageSize, rdParameter, viParameter, dimensions).then(
            (newPageUrl: IPrpResponse) => {
                window.history.pushState({}, null, newPageUrl.url);
                setIsLoading(false);
                setPageSortKey(data.Sorting.SelectedSortOption.Key);
            },
        );
    };

    if (!isFetchComplete) {
        return (
            <section className="tsc-container">
                <p className="prp__fetch-loading">{t('loading')}</p>
            </section>
        );
    }

    if (
        isFetchComplete &&
        data.Paging.TotalRecords === 0 &&
        !data.DimensionStates.some((state) => state.Refinements.length > 0)
    ) {
        return (
            <section className="tsc-container">
                <PrpNoSearchResults
                    noResult={{
                        heading: t('noSearchResultHeading'),
                        text: t('noSearchResultText'),
                        buttonText: t('noSearchResultButtonText'),
                    }}
                />
            </section>
        );
    }

    return (
        <>
            {showFraudModal && <FraudModal onCloseClickListener={() => setShowFraudModal(false)} />}
            <section id="to-scroll-position" className="m-auto w-full max-w-[1920px] px-5">
                {isSearchPage && (
                    <h2 className="prp__showing-results">
                        {t('showingResultsFor')} &quot;{decodeURIComponent(keyword)}&quot;
                    </h2>
                )}
                {isOnAirPage && <OnAirButtons className="prp-filter-panel__on-air--on-page" />}
                {
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                    <div className="prp" onKeyDown={prpKeyDownListener}>
                        {isLoading && <div className="prp__loading">Loading</div>}
                        {jsonLD.length > 0 && (
                            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLD }} />
                        )}
                        {isFilterPanelOpen && (
                            <PrpFilterPanel
                                dimensionStates={data.DimensionStates}
                                queryDims={data.QueryDims}
                                onCloseListener={() => setIsFilterPanelOpen(false)}
                                onFilterChangeListener={onFilterChange}
                                onClearAllClickListener={removeAllAppliedFilters}
                                appliedFilters={appliedFilters}
                                totalProductCount={data.Paging.TotalRecords}
                                isSearchPage={isSearchPage}
                                isOnAirPage={isOnAirPage}
                                isExpandedFilter={isExpandedFilter}
                                expandedFilter={expandedFilter}
                                loading={isLoading}
                            />
                        )}
                        <div className="prp__wrapper">
                            <div className="prp__count-and-shorting">
                                <div className="prp__count-and-shorting__product-count">
                                    {data.Paging.TotalRecords} {t('products')}
                                </div>
                                <button className="prp__count-and-shorting__filter-button" onClick={filterPanelHandler}>
                                    {t('filter')}
                                </button>
                                <select
                                    className="prp__count-and-shorting__shorting"
                                    defaultValue={data.Sorting.SelectedSortOption.Key}
                                    onChange={sortChange}
                                >
                                    {data.Sorting.SortOptions.map((sortOption) => (
                                        <option key={sortOption.Key} value={sortOption.Key}>
                                            {sortOption.DisplayName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {removedFilters.length > 0 && (
                                <div className="prp__applied-filters">
                                    {removedFilters.map((filter) => (
                                        <button
                                            key={filter.DimensionId}
                                            className="prp__applied-filters__filter"
                                            onClick={() => removeAppliedFilter(filter.DimensionType, filter.UrlSlug)}
                                        >
                                            {(filter.DimensionType === 'TSC_d_reviews' ||
                                                filter.DimensionType.toLowerCase() === 'review') && (
                                                <img
                                                    className="prp-filter-panel__filter-list__item-label-raiting--self prp__applied-filters__filter__stars"
                                                    src={ratingSvgMapping[filter.DimensionName[0]]}
                                                    alt=""
                                                />
                                            )}
                                            <span className="prp__applied-filters__filter__name">
                                                {filter.DimensionName}
                                            </span>
                                            <svg
                                                width={12}
                                                height={12}
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="prp__applied-filters__filter__remove"
                                            >
                                                <g
                                                    stroke="#000"
                                                    strokeWidth={2}
                                                    fill="none"
                                                    fillRule="evenodd"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M1.238 1.238l9.524 9.524M10.762 1.238l-9.524 9.524" />
                                                </g>
                                            </svg>
                                        </button>
                                    ))}
                                    <button
                                        className="prp__applied-filters__filter prp__applied-filters__filter--clear-all"
                                        onClick={removeAllAppliedFilters}
                                    >
                                        {t('clearAll')}
                                    </button>
                                </div>
                            )}
                            {
                                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                                <div className="prp__product-grid" onKeyDown={(e) => productGridKeyListener(e)}>
                                    {cards}
                                </div>
                            }
                        </div>
                    </div>
                }
                <div className="horizontal-divider" style={{ marginTop: '20px' }}></div>
                <div className="prp__pagination-desc">
                    <span className="prp__pagination-desc__display-text">{t('displayingItems')}:</span>
                    <span className="prp__pagination-desc__product-count-text">
                        {(data.Paging.CurrentPage - 1) * (isAdCardAvailable ? currentPageSize - 1 : currentPageSize) +
                            1}{' '}
                        -{' '}
                        {Math.min(
                            data.Paging.CurrentPage * (isAdCardAvailable ? currentPageSize - 1 : currentPageSize),
                            data.Paging.TotalRecords,
                        )}{' '}
                        {t('outOf')} {data.Paging.TotalRecords}
                    </span>
                </div>
                <div className="horizontal-divider" style={{ marginTop: '15px' }}></div>
                <div className="prp__pagination">
                    <a
                        href={buildPageUrl(
                            currentPage - 1,
                            data.Sorting.SelectedSortOption.Key,
                            keyword,
                            isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                            rdParameter,
                            viParameter,
                            dimensions,
                        )}
                        onClick={
                            currentPage === 1
                                ? (e) => e.preventDefault()
                                : (e) => {
                                      e.preventDefault();
                                      setIsLoading(true);
                                      navigateToPage(
                                          currentPage - 1,
                                          data.Sorting.SelectedSortOption.Key,
                                          keyword,
                                          isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                          rdParameter,
                                          viParameter,
                                          dimensions,
                                      ).then((prpUrl: IPrpResponse) => {
                                          setIsLoading(false);
                                          window.history.pushState({}, null, prpUrl.url);
                                      });
                                      scrollToTopOfPRP();
                                  }
                        }
                        className="prp__pagination__prev-link"
                        style={{ opacity: currentPage === 1 ? 0 : 1 }}
                    >
                        <span className="visually-hidden">{t('previous')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="chevron" viewBox="0 0 7 10">
                            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                                <g transform="translate(-1161 -3756)">
                                    <path className="chevron-fill" d="M0 0H1200V4300H0z"></path>
                                    <g
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        transform="translate(0 3625)"
                                        className="chevron-stroke"
                                    >
                                        <g transform="translate(0 56)">
                                            <g transform="translate(30)">
                                                <g transform="translate(0 73.5)">
                                                    <g transform="translate(1127.5 2.5)">
                                                        <g transform="rotate(-180 7 4.5)">
                                                            <g transform="rotate(-90 7 2)">
                                                                <path
                                                                    d="M2 -2L6 2 2 6"
                                                                    transform="rotate(90 4 2)"
                                                                ></path>
                                                            </g>
                                                        </g>
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </a>
                    <div className="prp__pagination__pages">
                        {paginationIndices.map((index) => (
                            <a
                                href={buildPageUrl(
                                    index,
                                    data.Sorting.SelectedSortOption.Key,
                                    keyword,
                                    isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                    rdParameter,
                                    viParameter,
                                    dimensions,
                                )}
                                key={index}
                                className={classNames('prp__pagination__pages__page inline-block', {
                                    'prp__pagination__pages__page--current': index === data.Paging.CurrentPage,
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsLoading(true);
                                    navigateToPage(
                                        index,
                                        data.Sorting.SelectedSortOption.Key,
                                        keyword,
                                        isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                        rdParameter,
                                        viParameter,
                                        dimensions,
                                    ).then((prpUrl: IPrpResponse) => {
                                        setIsLoading(false);
                                        window.history.pushState({}, null, prpUrl.url);
                                    });
                                    scrollToTopOfPRP();
                                }}
                            >
                                {index}
                            </a>
                        ))}
                    </div>
                    {currentPage < data.Paging.TotalPages ? (
                        <a
                            href={buildPageUrl(
                                currentPage + 1,
                                data.Sorting.SelectedSortOption.Key,
                                keyword,
                                isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                rdParameter,
                                viParameter,
                                dimensions,
                            )}
                            onClick={
                                currentPage === data.Paging.TotalPages
                                    ? (e) => e.preventDefault()
                                    : (e) => {
                                          e.preventDefault();
                                          setIsLoading(true);
                                          navigateToPage(
                                              currentPage + 1,
                                              data.Sorting.SelectedSortOption.Key,
                                              keyword,
                                              isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                              rdParameter,
                                              viParameter,
                                              dimensions,
                                          ).then((prpUrl: IPrpResponse) => {
                                              setIsLoading(false);
                                              window.history.pushState({}, null, prpUrl.url);
                                          });
                                          scrollToTopOfPRP();
                                      }
                            }
                            className="prp__pagination__next-link"
                            style={{ opacity: currentPage === data.Paging.TotalPages ? 0 : 1 }}
                        >
                            <span className="visually-hidden">{t('next')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="chevron" viewBox="0 0 7 10">
                                <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                                    <g transform="translate(-1161 -3757)">
                                        <path className="chevron-fill" d="M0 0H1200V4300H0z"></path>
                                        <g
                                            className="chevron-stroke"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            transform="translate(0 3625)"
                                        >
                                            <g transform="translate(0 56)">
                                                <g transform="translate(30)">
                                                    <g transform="translate(0 73.5)">
                                                        <g transform="translate(1127.5 2.5)">
                                                            <g transform="rotate(-90 7 2)">
                                                                <path
                                                                    d="M2 -2L6 2 2 6"
                                                                    transform="rotate(90 4 2)"
                                                                ></path>
                                                            </g>
                                                        </g>
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </svg>
                        </a>
                    ) : (
                        <div className="prp__pagination__next-link" style={{ opacity: 0 }}></div>
                    )}
                </div>
            </section>
        </>
    );
};

export default Prp;
